import { useArModelBlobs } from './useArModelBlobs';
import type { MapStyleKey } from './MapboxMap';

interface ArWallPreviewProps {
  active: boolean;
  imageUrl: string;
  sizeLabel: string;
  location: string;
  mapStyleKey: MapStyleKey;
  className?: string;
}

// Static (no zoom/AR controls) version of the framed-print-on-a-wall render
// used by ArPreviewModal — for spots like the Order sheet that just want
// the same visual as a preview thumbnail, not the full interactive 3D view.
// Renders with a transparent background — the caller is expected to supply
// the wall-texture backdrop itself (see App.tsx's Order sheet, which tiles
// it across the whole preview area, not just this component's own box, so
// the pattern stays seamless across both).
//
// To make the print look bigger than model-viewer's own "auto" framing
// (which leaves a fair bit of headroom around the model), size this
// component's own box LARGER than whatever visual footprint the caller
// reserves for it, rather than CSS-scaling the model-viewer element up
// inside a same-size box — that seemed simpler at first but scales the
// drop-shadow's needed clearance right along with the frame, so it starts
// clipping against the box edges well before the frame itself would.
// Growing the box instead keeps model-viewer's own natural margin (which
// scales proportionally, shadow included) and just makes that margin
// bigger in absolute pixels. Caller passes the enlarged sizing via
// className (e.g. inset several px negative on all sides of its reserved
// layout space) — see App.tsx's Order sheet. That className MUST include a
// position value (absolute/relative/fixed) of its own — this component
// doesn't set one itself, since a hardcoded `relative` here would fight
// whatever position the caller's className sets (both target the same CSS
// property; picture the box silently collapsing to zero height, not an
// error) — and inset-based sizing only works under `position: absolute`.
export function ArWallPreview({ active, imageUrl, sizeLabel, location, mapStyleKey, className = '' }: ArWallPreviewProps) {
  const { status, urls } = useArModelBlobs(active, imageUrl, sizeLabel, mapStyleKey);

  return (
    <div className={className}>
      {status === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="size-[28px] rounded-full border-2 border-[#e4e7ec] border-t-[#404342] animate-spin" />
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 flex items-center justify-center px-[16px]">
          <p className="font-['Roboto',sans-serif] font-normal text-[#dc2626] text-[16px] text-center">
            Couldn't build preview.
          </p>
        </div>
      )}

      {status === 'ready' && urls && (
        <model-viewer
          src={urls.glbUrl}
          ios-src={urls.usdzUrl}
          alt={`${sizeLabel} map print of ${location}`}
          // Same shadow/lighting treatment as the full 3D view — see
          // ArPreviewModal.tsx for why the native shadow is off in favor
          // of a CSS drop-shadow. No AR attributes or zoom controls here;
          // this is a static preview thumbnail, not an interactive view.
          shadow-intensity="0"
          camera-orbit="0deg 0deg auto"
          style={{
            width: '100%',
            height: '100%',
            backgroundColor: 'transparent',
            filter: 'drop-shadow(10px 14px 16px rgba(0,0,0,0.35))',
          }}
        />
      )}
    </div>
  );
}
